import { Ty } from '../ty';
import type { Def } from './erod-schema';
import { Assert } from '@cotera/utilities';
import _ from 'lodash';

export type JsonSchema = Record<string, unknown>;

/**
 * LLM provider identifiers for schema conversion.
 * Different providers have different JSON Schema support:
 *
 * - **openai**: Strict mode - all fields required, additionalProperties: false,
 *   nullable via oneOf with null. Supports records with additionalProperties.
 *
 * - **anthropic**: Standard JSON Schema, similar to OpenAI. Supports records.
 *
 * - **google**: Gemini has limitations: additionalProperties in function calling
 *   causes MALFORMED_FUNCTION_CALL. Records are converted to type: object
 *   (accept any) as fallback. oneOf with null may have issues in some contexts.
 *
 * - **default**: Safe fallback - OpenAI-style (all required, nullable via oneOf).
 */
export type LlmProvider = 'openai' | 'anthropic' | 'google' | 'default';

/**
 * Maps AI SDK model.provider strings to our LlmProvider strategy.
 * Used when converting schemas for tool parameters.
 */
export const AI_SDK_PROVIDER_TO_LLM: Record<string, LlmProvider> = {
  'openai.responses': 'openai',
  'openai.chat': 'openai',
  'anthropic.messages': 'anthropic',
  'google.generative-ai': 'google',
  'google-vertex.responses': 'google',
};

/**
 * Resolves provider from AI SDK model provider string.
 * Returns 'default' for unknown providers.
 */
export function resolveLlmProvider(
  aiSdkProvider: string | undefined
): LlmProvider {
  if (aiSdkProvider === undefined) {
    return 'default';
  }
  return AI_SDK_PROVIDER_TO_LLM[aiSdkProvider] ?? 'default';
}

/** Options for erodToJsonSchema */
export type ErodToJsonSchemaOptions = {
  strict?: boolean;
  /** Provider-specific schema transformation. Default: 'default' (OpenAI-compatible) */
  provider?: LlmProvider;
  /** @internal Used for cycle detection in recursive schemas (e.g. document with Schema.lazy) */
  _visited?: WeakSet<object>;
};

/** Max recursion depth for ExtendedAttributeType schema. Avoids circular refs for JSON serialization. */
const EXTENDED_ATTRIBUTE_TYPE_MAX_DEPTH = 10;

/**
 * Builds the ExtendedAttributeType schema at a given depth. At max depth, nested
 * struct/array/record use a placeholder to avoid circular references that break
 * JSON.stringify when the AI SDK serializes tool schemas for API calls.
 */
function extendedAttributeTypeSchemaAtDepth(
  provider: LlmProvider,
  depth: number
): JsonSchema {
  const useAdditionalProperties = provider !== 'google';
  const atMaxDepth = depth >= EXTENDED_ATTRIBUTE_TYPE_MAX_DEPTH;

  const nestedSchema: JsonSchema = atMaxDepth
    ? { type: 'object' }
    : extendedAttributeTypeSchemaAtDepth(provider, depth + 1);

  const structFieldsSchema: JsonSchema = useAdditionalProperties
    ? { type: 'object', additionalProperties: nestedSchema }
    : { type: 'object' };

  const arrayRecordTSchema: JsonSchema =
    useAdditionalProperties && !atMaxDepth ? nestedSchema : { type: 'object' };

  const tySchema: JsonSchema = {
    oneOf: [
      {
        type: 'object',
        properties: {
          k: { const: 'primitive' },
          t: {
            type: 'string',
            enum: [...Ty.PRIMITIVE_ATTRIBUTE_TYPES],
          },
        },
        required: ['k', 't'],
        additionalProperties: false,
      },
      {
        type: 'object',
        properties: {
          k: { const: 'enum' },
          t: {
            type: 'array',
            items: { type: 'string' },
          },
        },
        required: ['k', 't'],
        additionalProperties: false,
      },
      {
        type: 'object',
        properties: {
          k: { const: 'struct' },
          fields: structFieldsSchema,
        },
        required: ['k', 'fields'],
        additionalProperties: false,
      },
      {
        type: 'object',
        properties: {
          k: { enum: ['array', 'record'] },
          t: arrayRecordTSchema,
        },
        required: ['k', 't'],
        additionalProperties: false,
      },
      {
        type: 'object',
        properties: {
          k: { const: 'id' },
          name: { type: 'string' },
          t: { enum: ['string', 'int'] },
        },
        required: ['k', 'name', 't'],
        additionalProperties: false,
      },
      {
        type: 'object',
        properties: {
          k: { const: 'range' },
          start: { type: 'integer' },
          end: { type: 'integer' },
          t: { const: 'int' },
        },
        required: ['k', 'start', 'end', 't'],
        additionalProperties: false,
      },
    ],
  };

  return {
    type: 'object',
    properties: {
      ty: tySchema,
      nullable: { type: 'boolean' },
      tags: {
        type: 'array',
        items: { type: 'string' },
      },
    },
    required: ['ty', 'nullable', 'tags'],
    additionalProperties: false,
  };
}

/**
 * JSON Schema for Ty.ExtendedAttributeType - the structure the LLM must provide
 * for outputShape and similar fields. Describes the full discriminated union
 * so the LLM outputs proper { ty: { k: 'struct', fields: {} }, ... } not ty: "object".
 * Uses depth-limited recursion to avoid circular references that would break
 * JSON.stringify when the AI SDK serializes schemas for OpenAI/Anthropic API calls.
 *
 * @param provider - For 'google', struct/record use type:object without additionalProperties
 *   in nested ExtendedAttributeType (Gemini rejects additionalProperties in function params)
 */
export function extendedAttributeTypeJsonSchema(
  nullable: boolean,
  provider: LlmProvider = 'default'
): JsonSchema {
  const baseSchema = extendedAttributeTypeSchemaAtDepth(provider, 0);

  if (nullable) {
    return { oneOf: [baseSchema, { type: 'null' }] };
  }
  return baseSchema;
}

/** Context passed through recursive conversion */
type ConversionContext = {
  provider: LlmProvider;
  opts?: ErodToJsonSchemaOptions;
};

/**
 * Converts Ty.ExtendedAttributeType (or shorthand) to JSON Schema for values.
 * Used for struct fields, array items, etc.
 */
function tyToJsonSchema(
  ty: Ty.ExtendedAttributeType,
  ctx: ConversionContext
): JsonSchema {
  const { ty: innerTy, nullable } = ty;
  const { provider } = ctx;

  const makeSchema = (schema: JsonSchema): JsonSchema =>
    nullable ? { oneOf: [schema, { type: 'null' }] } : schema;

  switch (innerTy.k) {
    case 'primitive': {
      if (innerTy.t === 'super') {
        // LLM providers require an explicit type; bare `{}` serializes as type None.
        return makeSchema({ type: 'object' });
      }
      const typeMap: Record<string, JsonSchema> = {
        int: { type: 'integer' },
        float: { type: 'number' },
        boolean: { type: 'boolean' },
        timestamp: { type: 'string', format: 'date-time' },
        string: { type: 'string' },
        day: { type: 'string' },
        month: { type: 'string' },
        year: { type: 'string' },
      };
      return makeSchema(typeMap[innerTy.t] ?? { type: 'string' });
    }
    case 'array':
      return makeSchema({
        type: 'array',
        items: tyToJsonSchema(innerTy.t, ctx),
      });
    case 'enum':
      if (innerTy.t.length > 0) {
        return makeSchema({ type: 'string', enum: [...innerTy.t] });
      }
      return nullable ? { type: 'null' } : { not: {} };
    case 'record': {
      // Gemini rejects additionalProperties in function calling params
      if (provider === 'google') {
        return makeSchema({ type: 'object' });
      }
      return makeSchema({
        type: 'object',
        additionalProperties: tyToJsonSchema(Ty.nn(innerTy.t), ctx),
      });
    }
    case 'range':
      return makeSchema({
        type: 'number',
        minimum: innerTy.start,
        maximum: innerTy.end,
      });
    case 'struct':
      return makeSchema({
        type: 'object',
        required: Object.keys(innerTy.fields).filter((key) => {
          const fieldTy = innerTy.fields[key];
          return fieldTy !== undefined && !Ty.shorthandToTy(fieldTy).nullable;
        }),
        properties: _.mapValues(innerTy.fields, (fieldTy) =>
          tyToJsonSchema(Ty.shorthandToTy(fieldTy), ctx)
        ),
        additionalProperties: false,
      });
    default:
      return Assert.unreachable(innerTy);
  }
}

/** Defs that implement toJsonSchema for custom conversion (e.g. ExtendedAttributeType) */
type DefWithToJsonSchema = Def<unknown> & {
  toJsonSchema?: (opts?: {
    nullable?: boolean;
    provider?: LlmProvider;
  }) => JsonSchema;
};

/**
 * Converts an Erod Def to JSON Schema suitable for LLM tool parameters.
 * Handles the full Erod schema subset including struct, array, record,
 * discriminatedUnion, lazy, and ExtendedAttributeType.
 *
 * @param def - The Erod schema definition
 * @param opts - Options including provider for provider-specific transformations
 */
export function erodToJsonSchema(
  def: Def<unknown>,
  opts?: ErodToJsonSchemaOptions
): JsonSchema {
  const provider = opts?.provider ?? 'default';

  // Cycle detection: recursive schemas (e.g. DocumentNodeSchema with Schema.lazy)
  // cause infinite recursion. Return placeholder when we've seen this def.
  const visited = opts?._visited ?? new WeakSet<object>();
  const effectiveOpts: ErodToJsonSchemaOptions = {
    ...opts,
    provider,
    _visited: visited,
  };
  const ctx: ConversionContext = { provider, opts: effectiveOpts };

  if (visited.has(def as object)) {
    return { type: 'object' };
  }
  visited.add(def as object);

  const defWithCustom = def as DefWithToJsonSchema;
  if (typeof defWithCustom.toJsonSchema === 'function') {
    try {
      const result = defWithCustom.toJsonSchema({
        nullable: def.isNullable,
        provider,
      });
      return def.description
        ? { ...result, description: def.description }
        : result;
    } finally {
      visited.delete(def as object);
    }
  }

  const shape = (def as Def<unknown> & { shape?: { t: string } }).shape;
  if (shape === undefined) {
    visited.delete(def as object);
    return { type: 'string' };
  }

  let baseSchema: JsonSchema;
  try {
    baseSchema = ((): JsonSchema => {
      switch (shape.t) {
        case 'base': {
          return tyToJsonSchema(def.ty, ctx);
        }
        case 'array': {
          const eltSchema = (shape as { elementSchema: Def<unknown> })
            .elementSchema;
          return {
            type: 'array',
            items: erodToJsonSchema(eltSchema, effectiveOpts),
          };
        }
        case 'record': {
          const valueSchema = (shape as { recordValueSchema: Def<unknown> })
            .recordValueSchema;
          if (provider === 'google') {
            return { type: 'object' };
          }
          return {
            type: 'object',
            additionalProperties: erodToJsonSchema(valueSchema, effectiveOpts),
          };
        }
        case 'recordWithKey': {
          const valueSchema = (shape as { valueSchema: Def<unknown> })
            .valueSchema;
          if (provider === 'google') {
            return { type: 'object' };
          }
          return {
            type: 'object',
            additionalProperties: erodToJsonSchema(valueSchema, effectiveOpts),
          };
        }
        case 'struct': {
          const fieldsSchema = (
            shape as {
              fieldsSchema: Record<string, Def<unknown>>;
            }
          ).fieldsSchema;
          const properties: Record<string, JsonSchema> = {};
          const required: string[] = [];
          for (const [key, fieldDef] of Object.entries(fieldsSchema)) {
            properties[key] = erodToJsonSchema(fieldDef, effectiveOpts);
            required.push(key);
          }
          return {
            type: 'object',
            properties,
            required,
            additionalProperties: false,
          };
        }
        case 'discriminatedUnion': {
          const options = (shape as { options: readonly Def<unknown>[] })
            .options;
          return {
            oneOf: options.map((opt) => erodToJsonSchema(opt, effectiveOpts)),
          };
        }
        case 'lazy': {
          const get = (shape as { get: () => Def<unknown> }).get;
          return erodToJsonSchema(get(), effectiveOpts);
        }
        default:
          return tyToJsonSchema(def.ty, ctx);
      }
    })();
  } finally {
    visited.delete(def as object);
  }

  if (def.isNullable) {
    // Avoid double-wrapping: tyToJsonSchema already adds oneOf for nullable ty
    const alreadyAllowsNull =
      Array.isArray(baseSchema['oneOf']) &&
      baseSchema['oneOf'].some(
        (v) =>
          typeof v === 'object' &&
          v !== null &&
          (v as Record<string, unknown>)['type'] === 'null'
      );
    if (!alreadyAllowsNull) {
      const nullableSchema = {
        oneOf: [baseSchema, { type: 'null' }],
      };
      return def.description
        ? { ...nullableSchema, description: def.description }
        : nullableSchema;
    }
  }

  if (def.description) {
    return { ...baseSchema, description: def.description };
  }
  return baseSchema;
}
